import { useCallback, useEffect, useRef, useState } from 'react';

const cameraErrorMessage = error => {
  if (!navigator.mediaDevices?.getUserMedia) return 'مرورگر شما دسترسی مستقیم به دوربین را پشتیبانی نمی‌کند.';
  if (error?.name === 'NotAllowedError') return 'برای عکس گرفتن، دسترسی دوربین را از تنظیمات مرورگر فعال کنید.';
  if (error?.name === 'NotFoundError') return 'دوربینی روی این دستگاه پیدا نشد.';
  if (error?.name === 'NotReadableError') return 'دوربین در برنامهٔ دیگری در حال استفاده است.';
  return 'راه‌اندازی دوربین ممکن نشد. می‌توانید از گالری عکس انتخاب کنید.';
};

export default function useCamera() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const requestRef = useRef(0);
  const [facingMode, setFacingMode] = useState('environment');
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoom, setZoomState] = useState(null);
  const [zoomRange, setZoomRange] = useState(null);

  const stop = useCallback(() => {
    requestRef.current += 1;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setTorchOn(false);
  }, []);

  const start = useCallback(async mode => {
    stop();
    const request = requestRef.current;
    setStatus('loading');
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      if (request !== requestRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      const track = stream.getVideoTracks()[0];
      const capabilities = track?.getCapabilities?.() || {};
      const settings = track?.getSettings?.() || {};
      setTorchSupported(Boolean(capabilities.torch));
      if (capabilities.zoom) {
        const initialZoom = settings.zoom ?? capabilities.zoom.min;
        setZoomRange({ min: capabilities.zoom.min, max: capabilities.zoom.max, step: capabilities.zoom.step || 0.1 });
        setZoomState(initialZoom);
      } else {
        setZoomRange(null);
        setZoomState(null);
      }
      setStatus('ready');
    } catch (cameraError) {
      setStatus('error');
      setError(cameraErrorMessage(cameraError));
    }
  }, [stop]);

  useEffect(() => {
    start(facingMode);
    return stop;
  }, [facingMode, start, stop]);

  const switchCamera = useCallback(() => {
    setFacingMode(current => current === 'environment' ? 'user' : 'environment');
  }, []);

  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track || !torchSupported) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      setTorchSupported(false);
    }
  }, [torchOn, torchSupported]);

  const setZoom = useCallback(async value => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    const next = Number(value);
    if (!track || !zoomRange || !Number.isFinite(next)) return;
    try {
      await track.applyConstraints({ advanced: [{ zoom: next }] });
      setZoomState(next);
    } catch {}
  }, [zoomRange]);

  return {
    videoRef, facingMode, status, error, torchSupported, torchOn, zoom, zoomRange,
    start: () => start(facingMode), switchCamera, toggleTorch, setZoom, stop,
  };
}
